import {
  BarElement,
  CategoryScale,
  ChartData,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { IGetSummarySaleChart } from 'features/report/services/types/reportStaff';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { useContainerSize } from '../hooks/useContainerSize';

interface IncomeChartProps {
  data?: IGetSummarySaleChart[];
  isYearComparasion?: boolean;
  year?: number;
}

const IncomeChart: React.FC<IncomeChartProps> = ({
  data = [],
  isYearComparasion = false,
  year = 0,
}) => {
  const values = data?.map((item) => item.value);
  const oldValues = data?.map((item) => item.valueOld || 0);
  const allValues = [...values, ...oldValues];
  const maxValue = Math.max(...allValues, 1);
  const roundedMax = Math.ceil(maxValue / 100) * 100;

  const defaultColor = '#3AADD9';
  const comparisonColor = '#4B63C7';

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: isYearComparasion,
          external: isYearComparasion
            ? undefined
            : function (context: any) {
                let tooltipEl = document.getElementById('chartjs-tooltip');

                if (!tooltipEl) {
                  tooltipEl = document.createElement('div');
                  tooltipEl.id = 'chartjs-tooltip';
                  tooltipEl.innerHTML = '<div></div>';
                  document.body.appendChild(tooltipEl);
                }

                const tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = '0';
                  return;
                }

                tooltipEl.style.opacity = '1';
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.padding = '8px 12px';
                tooltipEl.style.background = 'white';
                tooltipEl.style.borderRadius = '16px';
                tooltipEl.style.boxShadow = '0px 2px 10px rgba(0, 0, 0, 0.1)';
                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.transition = 'opacity 0.2s';

                const value = tooltipModel.dataPoints[0].formattedValue;

                tooltipEl.innerHTML = `<div style="text-align: center;">$${value}</div>`;

                const position = context.chart.canvas.getBoundingClientRect();
                const barCenterX = position.left + tooltipModel.caretX;
                const tooltipWidth = tooltipEl.offsetWidth;

                tooltipEl.style.left = `${barCenterX - tooltipWidth / 2}px`;
                tooltipEl.style.top = `${
                  position.top + tooltipModel.caretY - 50
                }px`;
              },
          titleFont: {
            size: 18,
            family: 'Poppins',
            weight: 'bold',
          },
          bodyFont: {
            size: 16,
            family: 'Poppins',
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            font: {
              size: 16,
            },
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          grid: {
            drawBorder: false,
            color: '#f1f1f1',
          },
          border: {
            display: false,
          },
          ticks: {
            font: {
              size: 16,
            },
            callback: (value: number) => {
              return `${formatCurrency(value)}`;
            },
            stepSize: roundedMax / 5,
            max: roundedMax,
          },
        },
      },
    }),
    [roundedMax, isYearComparasion]
  );

  const chartData: ChartData<
    'bar',
    (number | [number, number] | null)[],
    unknown
  > | null = useMemo(() => {
    if (!data) return null;

    if (isYearComparasion) {
      return {
        labels: data?.map((i) => i.key),
        datasets: [
          {
            label: year ? (year - 1).toString() : '',
            data: data?.map((i) => i.valueOld || 0),
            backgroundColor: defaultColor,
            borderWidth: 0,
            borderRadius: 3,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
          },
          {
            label: year ? year.toString() : '',
            data: data?.map((i) => i.value),
            backgroundColor: comparisonColor,
            borderWidth: 0,
            borderRadius: 3,
            barPercentage: 0.9,
            categoryPercentage: 0.8,
          },
        ],
      };
    } else {
      return {
        labels: data?.map((i) => i.key),
        datasets: [
          {
            label: '',
            data: data?.map((i) => i.value),
            backgroundColor: defaultColor,
            borderColor: '#1e293b',
            borderRadius: 3,
            barPercentage: 1,
            hoverShadowColor: 'rgba(0, 0, 0, 0.1)',
            hoverShadowBlur: 10,
            hoverShadowOffsetX: 0,
            hoverShadowOffsetY: 4,
          },
        ],
      };
    }
  }, [data, isYearComparasion, year]);

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>Income</ChartTitle>
        {isYearComparasion && (
          <LegendContainer>
            <LegendItem>
              <ColorBox color={defaultColor} />
              <YearLabel>{year - 1}</YearLabel>
            </LegendItem>
            <LegendItem>
              <ColorBox color={comparisonColor} />
              <YearLabel>{year}</YearLabel>
            </LegendItem>
          </LegendContainer>
        )}
           <StatItem>
            <StatLabel>Total: </StatLabel>
            <StatValue>{formatCurrency(data?.reduce((sum,item)=> sum + item.value, 0))}</StatValue>
          </StatItem>
      </ChartHeader>

      <ChartWrapper>
        <Bar
          data={chartData as any}
          options={options as any}
          plugins={[
            CategoryScale,
            LinearScale,
            BarElement,
            Title,
            Tooltip,
            Legend,
          ]}
        />
      </ChartWrapper>
    </ChartContainer>
  );
};

export default IncomeChart;

const ChartContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const ChartTitle = styled.h2`
  font-size: 26px;
  color: #1e293b;
  margin: 0;
  text-transform: capitalize;

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const ChartWrapper = styled.div`
  flex: 1;
  min-height: 200px;
  width: 100%;
  position: relative;

  @media (max-width: 768px) {
    min-height: 180px;
  }

  @media (max-width: 480px) {
    min-height: 150px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Poppins', sans-serif;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorBox = styled.div`
  width: 40px;
  height: 20px;
  background-color: ${(props) => props.color};
  margin-bottom: 5px;
`;

const YearLabel = styled.span`
  color: #272727;
  font-size: 26px;
  font-weight: 300;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StatValue = styled.span`
  color: #C84B31;
  font-family: Poppins;
  font-weight: 600;
  font-size: 26px;
  line-height: 100%;
  letter-spacing: 0%;
`;

const StatLabel = styled.span`
  font-size: 24px;
  color: #1e293b;
  margin-right: 5px;
  font-family: Poppins;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0%;
  text-transform: uppercase;
`;